import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Power, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { AppIcon } from './AppIcon';
import { initials, type DesktopItem } from './items';

/**
 * Windows-style Start menu: search on top, "Pinned" grid, "Recommended" list, user + power in the footer.
 * Purely presentational — the caller supplies items and handles sign-out.
 * `placement` is "bottom" (centred above the dock, launcher) or "top" (top-left under the menu bar, when a window is open).
 */
export function StartMenu({ apps, system, userName, orgName, placement = 'bottom', onClose, onSignOut }: {
  apps: DesktopItem[]; system: DesktopItem[]; userName?: string; orgName?: string; placement?: 'bottom' | 'top'; onClose: () => void; onSignOut: () => void;
}) {
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const all = useMemo(() => [...apps, ...system], [apps, system]);
  const needle = q.trim().toLowerCase();
  const results = needle ? all.filter((i) => `${i.name} ${i.description ?? ''}`.toLowerCase().includes(needle)) : null;

  const open = (item: DesktopItem) => {
    onClose();
    if (item.href) window.location.assign(item.href);
    else if (item.to) nav(item.to);
  };
  const onSubmit = (e: React.FormEvent) => { e.preventDefault(); if (results?.[0]) open(results[0]); };

  const Tile = ({ item }: { item: DesktopItem }) => (
    <button type="button" className="sm-tile" onClick={() => open(item)}><AppIcon item={item} size={36} /><span>{item.name}</span></button>
  );
  const Row = ({ item }: { item: DesktopItem }) => (
    <button type="button" className="sm-row" onClick={() => open(item)}>
      <AppIcon item={item} size={30} />
      <span style={{ minWidth: 0 }}><b className="truncate">{item.name}</b><small className="truncate">{item.description ?? (item.kind === 'app' ? 'App' : 'OS')}</small></span>
    </button>
  );

  return (
    <>
      <div className="sm-backdrop" onClick={onClose} aria-hidden />
      <div className="sm" data-placement={placement} role="dialog" aria-label="Start menu">
        <div className="sm-body">
          <form className="sm-search" onSubmit={onSubmit} role="search">
            <Search size={16} strokeWidth={1.8} />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for apps, settings and people" aria-label="Search" />
          </form>

          {results ? (
            <section>
              <div className="sm-section-h"><h3>Best match</h3></div>
              {results.length === 0 ? <div className="sm-empty">Nothing matches “{q}”.</div> : <div className="sm-list">{results.map((i) => <Row key={i.id} item={i} />)}</div>}
            </section>
          ) : (
            <>
              <section>
                <div className="sm-section-h"><h3>Pinned</h3><Link to="/" className="sm-more" onClick={onClose}>All apps <ChevronRight size={14} /></Link></div>
                {apps.length === 0 && <div className="sm-empty">No apps yet — an administrator needs to grant you a role in an app.</div>}
                <div className="sm-grid">{apps.map((a) => <Tile key={a.id} item={a} />)}{system.filter((s) => s.to !== '/').slice(0, 2).map((s) => <Tile key={s.id} item={s} />)}</div>
              </section>
              <section>
                <div className="sm-section-h"><h3>Recommended</h3></div>
                <div className="sm-list">{system.filter((s) => s.to !== '/').map((s) => <Row key={s.id} item={s} />)}</div>
              </section>
            </>
          )}
        </div>
        <div className="sm-foot">
          <Link to="/account" className="sm-user" onClick={onClose}>
            <span className="dk-avatar">{initials(userName ?? '?')}</span>
            <span>{userName ?? '…'}<small>{orgName}</small></span>
          </Link>
          <button type="button" className="sm-power" title="Sign out" aria-label="Sign out" onClick={onSignOut}><Power size={18} strokeWidth={1.8} /></button>
        </div>
      </div>
    </>
  );
}
